/**
 * 権限グループ管理画面 (権限管理 SBI-8)。
 *
 * ControlPanel API の `/features` `/permission-groups` を使い、権限グループの一覧 / 作成 / 編集 /
 * 削除を行う。グループは「利用できる機能 (= 画面) の集合」で、機能はチェックボックスで選ぶ。
 * 画面構成と操作感は任意テキストテンプレート管理画面 (`FreeTextTemplateScreen`) に合わせている。
 *
 * - 削除は確認ダイアログを経て実行する。所属ユーザーがいるグループ / 最後の管理権限グループの
 *   削除はバックエンドが 409 で拒否するので、理由と対処を日本語でバナーに示す。
 * - 完了 (登録 / 更新 / 削除) は `NoticeDialog` のポップアップで知らせる。
 *
 * ⚠️ この画面の表示制御 (`permission_group_admin`) は利便性のため。最終的な拒否は常にバックエンド (403)。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { ApiError } from '../api/common';
import { PermissionGroupsClient } from '../api/permissionGroups';
import type { PermissionFeature, PermissionGroup, PermissionGroupSaveResult } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { useHistoryWarning } from '../hooks/useHistoryWarning';
import { featureLabels } from '../permissions/adminLabels';
import { permissionGroupTarget } from '../pages/masterHistoryTarget';

import { ConfirmDialog } from './ConfirmDialog';
import { MasterHistoryLink } from './MasterHistoryLink';
import { NoticeDialog } from './NoticeDialog';
import { PermissionGroupFormModal } from './PermissionGroupFormModal';
import { describeAdminError } from './permissionAdminMessages';
import { StatusBanner } from './StatusBanner';

export interface PermissionGroupAdminScreenProps {
  config: AppConfig;
}

type ModalState = null | { mode: 'create' } | { mode: 'edit'; group: PermissionGroup };

export function PermissionGroupAdminScreen({
  config,
}: PermissionGroupAdminScreenProps): React.JSX.Element {
  const { getIdToken } = useAuth();
  /** 保存後の「変更履歴の登録に失敗」警告 (マスタ変更履歴 SBI 8)。× で閉じるまで消えない */
  const { historyWarning, reportHistory } = useHistoryWarning('permission-group-history-warning');
  const apiClient = useMemo(
    () => new PermissionGroupsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [features, setFeatures] = useState<PermissionFeature[]>([]);
  const [modal, setModal] = useState<ModalState>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PermissionGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  /** 一覧取得の世代カウンタ (古いレスポンスで新しい結果を上書きしない防御) */
  const requestIdRef = useRef(0);

  /** グループ一覧を取得して反映する。 */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      if (currentRequestId !== requestIdRef.current) return;
      setGroups(res.items);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(describeAdminError(err).message);
    } finally {
      if (currentRequestId === requestIdRef.current) setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void reload();
    return () => {
      // アンマウント時に世代を進め、進行中リクエストの結果を破棄する。
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  /** 機能カタログ (チェックボックスの選択肢と一覧の表示名)。失敗したらエラーを出し、再読み込みで取り直す。 */
  const loadFeatures = useCallback(async (): Promise<void> => {
    try {
      const res = await apiClient.listFeatures();
      setFeatures(res.items);
    } catch (err) {
      setErrorMessage(describeAdminError(err).message);
    }
  }, [apiClient]);

  useEffect(() => {
    void loadFeatures();
  }, [loadFeatures]);

  const handleReload = useCallback((): void => {
    void reload();
    void loadFeatures();
  }, [loadFeatures, reload]);

  const handleSaved = useCallback(
    (saved: PermissionGroupSaveResult, action: '登録' | '更新'): void => {
      // モーダルはここで閉じるので、履歴の警告はモーダルではなく画面側に出す
      reportHistory(saved);
      setModal(null);
      setNoticeMessage(`権限グループ「${saved.name}」を${action}しました`);
      void reload();
    },
    [reload, reportHistory],
  );

  /** 編集中に他者が削除していた: お知らせを閉じた後、モーダルを閉じて一覧を取り直す。 */
  const handleDeletedByOthers = useCallback((): void => {
    setModal(null);
    void reload();
  }, [reload]);

  /** 削除確認ダイアログで確定されたときの削除処理。 */
  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    setErrorMessage(null);
    try {
      reportHistory(await apiClient.remove(deleteTarget.group_id));
      await reload();
      setNoticeMessage(`権限グループ「${deleteTarget.name}」を削除しました`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // 先に他者が削除済み: 削除したい状態にはなっているのでメッセージは出さず、一覧だけ更新する。
        await reload();
      } else {
        setErrorMessage(describeAdminError(err).message);
      }
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [apiClient, deleteTarget, reload, reportHistory]);

  return (
    <div className="permission-admin-page">
      <section
        className="section"
        aria-label="権限グループ管理"
        data-testid="permission-group-screen"
      >
        <div className="route-selector" data-testid="permission-group-toolbar">
          <h2 className="permission-admin-title">権限グループ管理</h2>
          <button
            type="button"
            className="primary create-button"
            data-testid="permission-group-create"
            onClick={() => setModal({ mode: 'create' })}
          >
            ＋ 新規作成
          </button>
          <div className="actions">
            <button
              type="button"
              data-testid="permission-group-reload"
              onClick={handleReload}
              disabled={loading}
            >
              再読み込み
            </button>
          </div>
        </div>
        {historyWarning}
      </section>

      <section className="section permission-admin-list-section">
        <div className="permission-admin-list-body" data-testid="permission-group-list-body">
          {errorMessage ? (
            <StatusBanner
              variant="error"
              onDismiss={() => setErrorMessage(null)}
              data-testid="permission-group-error"
            >
              {errorMessage}
            </StatusBanner>
          ) : null}

          <table
            className="points-table permission-admin-table"
            data-testid="permission-group-table"
          >
            <colgroup>
              <col className="permission-admin-col-name" />
              <col className="permission-admin-col-description" />
              <col />
              <col className="permission-admin-col-actions-history" />
            </colgroup>
            <thead>
              <tr>
                <th>グループ名</th>
                <th>説明</th>
                <th>利用できる機能</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && !loading ? (
                <tr>
                  <td colSpan={4} className="empty-state" data-testid="permission-group-empty">
                    登録された権限グループはありません
                  </td>
                </tr>
              ) : null}
              {groups.map((group) => (
                <tr key={group.group_id} data-testid={`permission-group-row-${group.group_id}`}>
                  <td>{group.name}</td>
                  <td className="permission-admin-multiline">{group.description}</td>
                  <td data-testid={`permission-group-features-${group.group_id}`}>
                    {group.features.length === 0
                      ? 'なし'
                      : featureLabels(group.features, features).join('、')}
                  </td>
                  <td className="row-actions">
                    <button
                      type="button"
                      onClick={() => setModal({ mode: 'edit', group })}
                      disabled={deleting}
                      data-testid={`permission-group-edit-${group.group_id}`}
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => setDeleteTarget(group)}
                      disabled={deleting}
                      data-testid={`permission-group-delete-${group.group_id}`}
                    >
                      削除
                    </button>
                    <MasterHistoryLink
                      target={permissionGroupTarget(group.group_id)}
                      subject={group.name}
                      testId={`permission-group-history-${group.group_id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {modal ? (
        <PermissionGroupFormModal
          mode={modal.mode}
          group={modal.mode === 'edit' ? modal.group : null}
          features={features}
          apiClient={apiClient}
          onSaved={handleSaved}
          onDeletedByOthers={handleDeletedByOthers}
          onClose={() => setModal(null)}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="権限グループの削除"
        message={
          deleteTarget
            ? `権限グループ「${deleteTarget.name}」を削除します。よろしいですか？\n削除は元に戻せません。所属ユーザーがいるグループは削除できません。`
            : ''
        }
        confirmLabel="削除"
        destructive
        confirmDisabled={deleting}
        cancelDisabled={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />

      <NoticeDialog
        open={noticeMessage !== null}
        message={noticeMessage ?? ''}
        onClose={() => setNoticeMessage(null)}
      />
    </div>
  );
}
